import React from 'react';
import { TrendingUp, Sparkles, Clock } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Bot } from '@/types/Bot';
import { BotCard } from './BotCard';

interface TrendingSectionProps {
  bots: Bot[];
  onBotClick: (bot: Bot) => void;
  onToggleFavorite: (botId: string) => void;
  isFavorite: (botId: string) => boolean;
}

export const TrendingSection: React.FC<TrendingSectionProps> = ({
  bots,
  onBotClick,
  onToggleFavorite,
  isFavorite
}) => {
  // Get trending bots (high popularity or marked as trending)
  const trendingBots = bots.filter(bot => bot.isTrending || bot.popularity > 90).slice(0, 3);
  
  // Get new bots (marked as new or recently added)
  const newBots = bots.filter(bot => bot.isNew).slice(0, 3);
  
  // Get editor's picks (highest rated with good review count)
  const editorsPicks = bots
    .filter(bot => bot.rating >= 4.6 && bot.reviewCount > 1000)
    .sort((a, b) => b.rating - a.rating)
    .slice(0, 3);

  const sections = [
    {
      title: "🔥 Trending Now",
      icon: TrendingUp,
      bots: trendingBots,
      description: "Popular AI tools gaining momentum"
    },
    {
      title: "✨ Editor's Picks",
      icon: Sparkles,
      bots: editorsPicks,
      description: "Top-rated tools recommended by our team"
    },
    {
      title: "🆕 Recently Added",
      icon: Clock,
      bots: newBots,
      description: "Fresh AI tools just added to our collection"
    }
  ];

  return (
    <section className="px-4 py-8">
      <div className="container mx-auto space-y-12">
        {sections.map((section, sectionIndex) => (
          section.bots.length > 0 && (
            <div key={sectionIndex}>
              <div className="flex items-center gap-3 mb-6">
                <section.icon className="w-6 h-6 text-primary" />
                <div>
                  <h2 className="text-2xl font-bold text-foreground">{section.title}</h2>
                  <p className="text-muted-foreground">{section.description}</p>
                </div>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {section.bots.map((bot) => (
                  <BotCard 
                    key={bot.id}
                    bot={bot}
                    onBotClick={onBotClick}
                    onToggleFavorite={onToggleFavorite}
                    isFavorite={isFavorite(bot.id)}
                  />
                ))}
              </div>
            </div>
          )
        ))}
      </div>
    </section>
  );
};